import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { getInvoiceDetails, updateInvoiceLineItem, sendDraftInvoiceToXero, syncInvoiceWithXero, voidInvoice } from '../services/invoiceService';
import { FaFileInvoiceDollar, FaArrowLeft, FaPaperPlane, FaSync, FaBan } from 'react-icons/fa';
import ConfirmationModal from '../components/ConfirmationModal';
import { isDemoMode } from '../utils/demoMode';

// Helper to format Firestore timestamp to a readable date string
const formatDate = (timestamp) => {
  if (!timestamp || !timestamp._seconds) return 'N/A';
  return new Date(timestamp._seconds * 1000).toLocaleDateString();
};

// Helper to format Firestore timestamp to a readable date and time string
const formatDateTime = (timestamp) => {
    if (!timestamp || !timestamp._seconds) return 'N/A';
    return new Date(timestamp._seconds * 1000).toLocaleString();
  };

const parseNumberInput = (value) => {
  if (value === '' || value === null || value === undefined) {
    return null;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const InvoiceDetailsPage = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnSearch = location.state?.returnSearch || '';
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isVoiding, setIsVoiding] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isEditingLineItem, setIsEditingLineItem] = useState(false);
  const [showVoidConfirm, setShowVoidConfirm] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [sendToXeroError, setSendToXeroError] = useState(null);
  const [lineItemForm, setLineItemForm] = useState({
    Quantity: '',
    UnitAmount: '',
  });

  const fetchInvoiceData = useCallback(async () => {
    setLoading(true);
    setError(null); // Clear previous fetch-related errors
    // Do not clear successMessage or sendToXeroError here, they are managed by the operations that set them.
    try {
      const data = await getInvoiceDetails(invoiceId);
      setInvoice(data);
    } catch (err) {
      setError(err.message || 'Failed to load invoice details.');
      setInvoice(null); // Ensure invoice is null if fetching fails
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    setSuccessMessage(''); // Clear on initial load or invoiceId change
    setSendToXeroError(null); // Clear on initial load or invoiceId change
    fetchInvoiceData();
  }, [fetchInvoiceData, invoiceId]);

  useEffect(() => {
    if (!invoice) {
      return;
    }

    setLineItemForm({
      Quantity: invoice.Quantity ?? '',
      UnitAmount: invoice.UnitAmount ?? '',
    });
  }, [invoice]);

  const handleSendToXero = async () => {
    if (!invoice || invoice.status !== 'draft') {
      setSendToXeroError("Invoice is not in draft status or not loaded.");
      return;
    }
    setIsSending(true);
    setSuccessMessage('');
    setSendToXeroError(null);

    let sendAttemptSucceeded = false;
    let operationMessage = '';

    try {
      const response = await sendDraftInvoiceToXero(invoice.id);
      sendAttemptSucceeded = true;
      operationMessage = response.message || "Invoice successfully sent to Xero and status updated.";
    } catch (err) {
      sendAttemptSucceeded = false;
      operationMessage = err.message || 'Failed to send invoice to Xero.';
    }

    // Attempt to refresh invoice data to reflect any backend status changes.
    // Do this regardless of the Xero send outcome.
    // If this refresh fails, we'll log it but still show the Xero operation outcome
    // with the potentially stale invoice data.
    try {
      const refreshedData = await getInvoiceDetails(invoiceId); // Use invoiceId from params
      setInvoice(refreshedData); // Update with fresh data
    } catch (refreshError) {
      console.error("Failed to refresh invoice data after Xero operation:", refreshError);
      // Do not set the main 'error' state here or nullify 'invoice'.
      // The user will see the Xero operation message with the invoice data
      // that was available before this refresh attempt.
    }

    // After the refresh attempt (successful or not), display the result of the send operation.
    if (sendAttemptSucceeded) {
      setSuccessMessage(operationMessage);
      setSendToXeroError(null); // Ensure no send error message is shown if send succeeded
    } else {
      setSendToXeroError(operationMessage);
      setSuccessMessage(''); // Ensure no success message is shown if send failed
    }

    setIsSending(false);
  };

  const handleSyncFromXero = async () => {
    if (!invoice?.id) return;

    setIsSyncing(true);
    setSendToXeroError(null);
    setSuccessMessage('');

    try {
      const response = await syncInvoiceWithXero(invoice.id);
      console.log('Xero sync response:', response);

      if (response.status === 'success') {
        // If fields were updated, refresh invoice data and show what was updated
        if (response.data.updatedFields?.length > 0) {
          const updatedInvoice = await getInvoiceDetails(invoice.id);
          setInvoice(updatedInvoice);
        }
        setSuccessMessage(response.message);
      } else {
        setSendToXeroError(response.message);
      }
    } catch (err) {
      console.error('Error syncing from Xero:', err);
      setSendToXeroError(err.message || 'Failed to sync from Xero');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleVoidInvoice = async () => {
    if (!invoice?.id) return;

    setIsVoiding(true);
    setSendToXeroError(null);
    setSuccessMessage('');
    setShowVoidConfirm(false);

    try {
      const response = await voidInvoice(invoice.id);
      console.log('Void invoice response:', response);

      if (response.status === 'success') {
        // Refresh invoice data to show updated status
        const updatedInvoice = await getInvoiceDetails(invoice.id);
        setInvoice(updatedInvoice);
        setSuccessMessage(response.message);
      } else {
        setSendToXeroError(response.message || 'Failed to void invoice');
      }
    } catch (err) {
      console.error('Error voiding invoice:', err);
      setSendToXeroError(err.message || 'Failed to void invoice');
    } finally {
      setIsVoiding(false);
    }
  };

  const handleLineItemInputChange = (event) => {
    const { name, value } = event.target;

    setLineItemForm((currentForm) => ({
      ...currentForm,
      [name]: value,
    }));
  };

  const handleCancelLineItemEdit = () => {
    if (invoice) {
      setLineItemForm({
        Quantity: invoice.Quantity ?? '',
        UnitAmount: invoice.UnitAmount ?? '',
      });
    }

    setIsEditingLineItem(false);
    setSendToXeroError(null);
  };

  const handleSaveLineItem = async () => {
    if (!invoice?.id) {
      return;
    }

    const quantity = parseNumberInput(lineItemForm.Quantity);
    const unitAmount = parseNumberInput(lineItemForm.UnitAmount);

    if (quantity === null || quantity <= 0) {
      setSendToXeroError('Quantity must be greater than 0.');
      return;
    }

    if (unitAmount === null || unitAmount < 0) {
      setSendToXeroError('Unit Amount must be 0 or greater.');
      return;
    }

    setIsUpdating(true);
    setSuccessMessage('');
    setSendToXeroError(null);

    try {
      const response = await updateInvoiceLineItem(invoice.id, {
        Quantity: quantity,
        UnitAmount: unitAmount,
      });
      const refreshedInvoice = await getInvoiceDetails(invoice.id);
      setInvoice(refreshedInvoice);
      setIsEditingLineItem(false);
      setSuccessMessage(response.message || 'Invoice line item updated successfully.');
    } catch (err) {
      console.error('Error updating invoice line item:', err);
      setSendToXeroError(err.message || 'Failed to update invoice line item.');
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading && !isSending && !invoice) { // Only show full page loading if invoice isn't already there
    return <p className="text-center text-gray-500 py-10">Loading invoice details...</p>;
  }

  // This 'error' state is ONLY for fatal errors from fetchInvoiceData
  // when the invoice object itself cannot be loaded (fetchInvoiceData sets invoice to null on error).
  if (error && !invoice) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <p className="text-red-500 text-lg">Error: {error}</p>
        <Link
          to={{ pathname: '/invoices', search: returnSearch }}
          className="mt-4 inline-block bg-green-600 hover:bg-green-600-dark text-white font-bold py-2 px-4 rounded"
        >
          <FaArrowLeft className="mr-2 inline" /> Back to Invoices
        </Link>
      </div>
    );
  }

  if (!invoice && !loading) { // If not loading and still no invoice (e.g. fetch completed, no data)
    return <p className="text-center text-gray-500 py-10">Invoice not found.</p>;
  }
  
  // If we have an invoice, render its details, along with any operational messages.
  // The main content rendering starts here, even if there's a sendToXeroError or successMessage.

  const displayQuantity = isEditingLineItem ? parseNumberInput(lineItemForm.Quantity) : invoice?.Quantity;
  const displayUnitAmount = isEditingLineItem ? parseNumberInput(lineItemForm.UnitAmount) : invoice?.UnitAmount;

  const amountDue = displayQuantity != null && displayUnitAmount != null
    ? (displayQuantity * displayUnitAmount).toFixed(2)
    : 'N/A';

  const displayStatus = invoice?.xero_submission_status === 'success' && invoice?.xero_invoice_status 
                        ? invoice.xero_invoice_status 
                        : invoice?.status;

  let statusHighlight = 'gray';
  if (invoice?.status === 'void' || invoice?.xero_invoice_status === 'VOIDED') {
    statusHighlight = 'red';
  } else if (invoice?.xero_submission_status === 'success' && invoice?.xero_invoice_status) {
    const xeroStatusLower = invoice.xero_invoice_status.toLowerCase();
    if (xeroStatusLower === 'paid') {
      statusHighlight = 'green';
    } else if (xeroStatusLower === 'authorised' || xeroStatusLower === 'submitted') {
      statusHighlight = 'blue'; // Or another appropriate color
    } else if (xeroStatusLower === 'draft') {
      statusHighlight = 'yellow';
    }
  } else if (invoice?.status) {
    const systemStatusLower = invoice.status.toLowerCase();
    if (systemStatusLower === 'paid') {
      statusHighlight = 'green';
    } else if (systemStatusLower === 'draft') {
      statusHighlight = 'yellow';
    }
  }

  // Determine if invoice can be voided (not already void, and not paid in Xero)
  const canVoid = invoice &&
                  invoice.status !== 'void' &&
                  invoice.xero_invoice_status !== 'VOIDED' &&
                  invoice.xero_invoice_status !== 'PAID';
  const canEditLineItem = invoice &&
                          invoice.status === 'draft' &&
                          invoice.status !== 'void' &&
                          !invoice.xeroInvoiceId &&
                          invoice.xero_submission_status !== 'success';


  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <FaFileInvoiceDollar className="mr-3" /> Invoice Details
        </h1>
        <div className="flex items-center">
            <button 
                onClick={fetchInvoiceData} 
                className="text-gray-900 hover:text-green-600 mr-4 p-2 rounded-full hover:bg-gray-200 transition duration-150"
                title="Refresh Invoice Data"
                disabled={loading || isSending || isUpdating}
            >
                <FaSync className={` ${loading && !isSending ? 'animate-spin' : ''}`} />
            </button>
            <button 
                onClick={() => {
                  if (returnSearch) {
                    navigate({ pathname: '/invoices', search: returnSearch });
                  } else {
                    navigate(-1);
                  }
                }} 
                className="text-gray-900 hover:text-green-600 underline flex items-center"
            >
                <FaArrowLeft className="mr-2" /> Back
            </button>
        </div>
      </div>

      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 border border-green-300 rounded">
          {successMessage}
        </div>
      )}

      {sendToXeroError && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded">
          Error: {sendToXeroError}
        </div>
      )}

      {!isDemoMode() && invoice && invoice.status === 'draft' && (
        <div className="mb-6 flex justify-end space-x-4">
          <button
            onClick={handleSendToXero}
            disabled={isSending || loading || isUpdating || isEditingLineItem || invoice.xero_submission_status === 'success'}
            className={`bg-jims-green hover:bg-jims-green-dark text-white font-bold py-2 px-4 rounded inline-flex items-center transition duration-300 ${isSending || loading || isUpdating || isEditingLineItem || invoice.xero_submission_status === 'success' ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isSending ? (
                <><FaSync className="animate-spin mr-2" /> Sending...</>
            ) : (
                <><FaPaperPlane className="mr-2" /> Send to Xero</>
            )}
          </button>
          
          {invoice.xero_submission_status === 'success' && invoice.xeroInvoiceId && (
            <button
              onClick={handleSyncFromXero}
              disabled={isSyncing || isUpdating || isEditingLineItem}
              className={`bg-jims-cyan hover:bg-jims-cyan-dark text-white font-bold py-2 px-4 rounded inline-flex items-center transition duration-300 ${isSyncing || isUpdating || isEditingLineItem ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <FaSync className={`mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
              {isSyncing ? 'Syncing...' : 'Sync from Xero'}
            </button>
          )}
        </div>
      )}

      <div className="bg-white shadow-xl rounded-lg p-6 md:p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
          {/* Column 1 */}
          <div>
            <DetailItem label="Invoice ID" value={invoice.id} />
            <DetailItem label="Invoice Number" value={invoice.InvoiceNumber} />
            <DetailItem
              label="Status"
              value={displayStatus ? displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1) : 'N/A'}
              highlight={statusHighlight}
            />
            {canVoid && (
              <div className="mb-2">
                <button
                  onClick={() => setShowVoidConfirm(true)}
                  className="text-red-600 hover:text-red-800 text-sm flex items-center mt-1"
                >
                  <FaBan className="mr-1" size={12} /> Void Invoice
                </button>
              </div>
            )}
            <DetailItem label="Job ID" value={invoice.jobId} linkTo={`/jobs-list/edit/${invoice.jobId}`} />
            <DetailItem label="Reference" value={invoice.Reference} />
            <DetailItem label="Invoice Date" value={formatDate(invoice.InvoiceDate)} />
            <DetailItem label="Due Date" value={formatDate(invoice.DueDate)} />
            <DetailItem label="Currency" value={invoice.Currency || 'AUD'} />
            <DetailItem label="Branding Theme" value={invoice.BrandingTheme || 'N/A'} />
          </div>

          {/* Column 2 */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3 border-b pb-2">Contact & Address</h3>
            <DetailItem label="Contact Name" value={invoice.ContactName} />
            <DetailItem label="Email Address" value={invoice.EmailAddress} />
            <DetailItem label="Xero Contact ID" value={invoice.XeroContactID || 'N/A'} />
            <p className="text-sm font-medium text-gray-500 mt-3">Postal Address:</p>
            <p className="text-gray-800">
                {(invoice.POAddressLine1 || '')}<br />
                {invoice.POAddressLine2 ? <>{invoice.POAddressLine2}<br /></> : ''}
                {invoice.POCity || ''}{invoice.PORegion ? `, ${invoice.PORegion}` : ''}<br />
                {invoice.POPostalCode || ''}{invoice.POCountry ? `, ${invoice.POCountry}` : ''}
            </p>
          </div>
        </div>

        {/* Line Items Section */}
        <div className="mt-6 pt-6 border-t">
            <div className="flex items-center justify-between gap-4 mb-3">
                <h3 className="text-xl font-semibold text-jims-cyan-dark">Line Item</h3>
                {canEditLineItem && !isEditingLineItem && (
                    <button
                        type="button"
                        onClick={() => {
                          setSuccessMessage('');
                          setSendToXeroError(null);
                          setIsEditingLineItem(true);
                        }}
                        className="text-sm font-semibold text-jims-cyan-dark hover:text-jims-cyan"
                    >
                        Edit Line Item
                    </button>
                )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-4">
                <DetailItem label="Description" value={invoice.Description} fullWidth={true} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-4 mt-4">
                {isEditingLineItem ? (
                  <>
                    <div className="mb-2">
                      <p className="text-sm font-medium text-gray-500">Quantity</p>
                      <input
                        type="number"
                        name="Quantity"
                        min="0"
                        step="0.01"
                        value={lineItemForm.Quantity}
                        onChange={handleLineItemInputChange}
                        className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-gray-800 focus:border-jims-cyan focus:outline-none"
                      />
                    </div>
                    <div className="mb-2">
                      <p className="text-sm font-medium text-gray-500">Unit Amount</p>
                      <input
                        type="number"
                        name="UnitAmount"
                        min="0"
                        step="0.01"
                        value={lineItemForm.UnitAmount}
                        onChange={handleLineItemInputChange}
                        className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-gray-800 focus:border-jims-cyan focus:outline-none"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <DetailItem label="Quantity" value={invoice.Quantity} />
                    <DetailItem label="Unit Amount" value={`$${invoice.UnitAmount ? invoice.UnitAmount.toFixed(2) : '0.00'}`} />
                  </>
                )}
                <DetailItem label="Discount" value={invoice.Discount !== null ? `${invoice.Discount}%` : 'N/A'} />
                <DetailItem label="Amount Due" value={`$${amountDue}`} highlight="blue" />
            </div>
            {isEditingLineItem && (
              <div className="mt-4 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCancelLineItemEdit}
                  disabled={isUpdating}
                  className="rounded border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveLineItem}
                  disabled={isUpdating}
                  className="rounded bg-jims-cyan px-4 py-2 text-sm font-semibold text-white hover:bg-jims-cyan-dark disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isUpdating ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
        </div>
        
        {/* Financial & Tracking Details */}
        <div className="mt-6 pt-6 border-t">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Financial & Tracking</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                <DetailItem label="Account Code" value={invoice.AccountCode} />
                <DetailItem label="Tax Type" value={invoice.TaxType} />
                {invoice.TrackingName1 && <DetailItem label={invoice.TrackingName1} value={invoice.TrackingOption1} />}
                {invoice.TrackingName2 && <DetailItem label={invoice.TrackingName2} value={invoice.TrackingOption2} />}
            </div>
        </div>

        {/* Xero Submission Details */}
        {(invoice.xero_submission_status || invoice.xeroInvoiceId) && (
            <div className="mt-6 pt-6 border-t">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Xero Submission Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                    {invoice.xero_submission_status && 
                        <DetailItem 
                            label="Xero Submission Status" 
                            value={invoice.xero_submission_status.charAt(0).toUpperCase() + invoice.xero_submission_status.slice(1)}
                            highlight={invoice.xero_submission_status === 'failed' ? 'red' : invoice.xero_submission_status === 'success' ? 'green' : 'gray'}
                        />
                    }
                    <DetailItem label="Xero Invoice ID" value={invoice.xeroInvoiceId || 'N/A'} />
                </div>
                {invoice.xero_submission_status === 'failed' && invoice.xero_submission_failure_reason && (
                    <div className="mt-4">
                        <DetailItem label="Xero Submission Failure Reason" value={invoice.xero_submission_failure_reason} fullWidth={true} highlight="red-text" />
                    </div>
                )}
            </div>
        )}

        {/* Timestamps */}
        <div className="mt-6 pt-6 border-t text-sm text-gray-500">
            <DetailItem label="Created At" value={formatDateTime(invoice.createdAt)} />
            <DetailItem label="Last Updated At" value={formatDateTime(invoice.updatedAt)} />
        </div>

      </div>

      <ConfirmationModal
        isOpen={showVoidConfirm}
        onClose={() => setShowVoidConfirm(false)}
        onConfirm={handleVoidInvoice}
        title="Void Invoice"
        message={`Are you sure you want to void invoice ${invoice?.InvoiceNumber || ''}? This action cannot be undone. If the invoice has been sent to Xero, it will also be voided there.`}
        confirmText="Void Invoice"
        variant="danger"
        isLoading={isVoiding}
        loadingText="Voiding..."
      />
    </div>
  );
};

const DetailItem = ({ label, value, linkTo, highlight, fullWidth }) => {
    const valueClasses = `text-gray-800 ${fullWidth ? 'col-span-full' : ''}`;
    let content = value;
    if (linkTo && value !== 'N/A') {
        content = <Link to={linkTo} className="text-green-600 hover:underline">{value}</Link>;
    }
    
    let highlightClass = '';
    if (highlight) {
        switch (highlight) {
            case 'yellow': highlightClass = 'bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full text-xs font-semibold'; break;
            case 'green': highlightClass = 'bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-xs font-semibold'; break;
            case 'blue': highlightClass = 'text-blue-600 font-bold'; break;
            case 'red': highlightClass = 'bg-red-100 text-red-800 px-2 py-0.5 rounded-full text-xs font-semibold'; break;
            case 'red-text': highlightClass = 'text-red-700 font-medium'; break;
            default: highlightClass = 'bg-gray-100 text-gray-800 px-2 py-0.5 rounded-full text-xs font-semibold'; break;
        }
        content = <span className={highlightClass}>{value}</span>;
    }

    return (
        <div className={`mb-2 ${fullWidth ? 'col-span-full' : ''}`}>
            <p className="text-sm font-medium text-gray-500">{label}</p>
            <p className={valueClasses}>{content}</p>
        </div>
    );
};

export default InvoiceDetailsPage;
